/**
 * Document slot row for the firm detail panel (ADR-0086 upload + delete;
 * Slice M / ADR-0100 exemptions) — extracted from firm-detail-panel.tsx.
 */

import { useRef } from "react";
import { Check, Trash2, Upload, X } from "lucide-react";
import type { FirmDocumentSlot, FirmFile } from "@command-center/shared";
import { cn } from "@/lib/utils";

/** MIME accept-string for each slot — must agree with the server allowlist
 *  in apps/worker/src/routes/client-success.ts. */
const SLOT_ACCEPT: Record<FirmDocumentSlot, string> = {
  logo: "image/png,image/jpeg,image/webp,image/svg+xml",
  w9: "application/pdf",
  malpractice: "application/pdf",
  mfaH: "application/pdf",
  mfaO: "application/pdf",
};

/** A document slot — green ✓ + filename link when present, muted ✗ + "Not on
 *  file" when absent. The whole row is click-to-upload when empty; when
 *  present, hover surfaces Replace + Delete buttons.
 *
 *  Disabled (no `onUpload`/`onDelete` props) when the parent has no wiring —
 *  renders the read-only ✓/✗ view, no affordances. */
export function DocumentRow({
  label,
  slot,
  file,
  testId,
  pending,
  onUpload,
  onDelete,
  exempt = false,
  exemptionPending = false,
  onMarkNotRequired,
  onRestoreRequired,
}: {
  label: string;
  slot: FirmDocumentSlot;
  file: FirmFile | null;
  testId: string;
  pending: boolean;
  onUpload?: (slot: FirmDocumentSlot, file: File) => void;
  onDelete?: (slot: FirmDocumentSlot) => void;
  /** Slice M (ADR-0100): the artifact is on the firm's Exempt Artifacts
   *  list. An absent + exempt slot renders "Not required" with a Restore
   *  link instead of the ✗ chase state. A PRESENT file wins visually — the
   *  exemption is moot while the doc is on file. */
  exempt?: boolean;
  exemptionPending?: boolean;
  /** Open the reason modal for this slot (renders only on ✗ rows). */
  onMarkNotRequired?: () => void;
  /** Clear the exemption — restore as required. */
  onRestoreRequired?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const present = file !== null;
  const wired = !!onUpload && !!onDelete;
  const accept = SLOT_ACCEPT[slot];

  function openPicker() {
    if (!wired || pending) return;
    inputRef.current?.click();
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f && onUpload) onUpload(slot, f);
  }

  return (
    <li
      className={cn(
        "group relative flex items-center gap-3 rounded-sm py-1 text-sm",
        wired && !present && !pending && "cursor-pointer hover:bg-accent/40",
        pending && "opacity-60",
      )}
      data-testid={testId}
      data-present={present ? "true" : "false"}
      data-pending={pending ? "true" : "false"}
      data-exempt={exempt && !present ? "true" : "false"}
      onClick={!present ? openPicker : undefined}
    >
      {present ? (
        <Check
          className="h-4 w-4 shrink-0 text-success"
          aria-label="On file"
        />
      ) : (
        <X
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-label="Not on file"
        />
      )}
      <span className="w-44 shrink-0 text-muted-foreground">{label}</span>
      {present ? (
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="min-w-0 truncate font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
          data-testid={`${testId}-link`}
          onClick={(e) => e.stopPropagation()}
        >
          {file.name}
        </a>
      ) : exempt ? (
        // Slice M (ADR-0100): exempted gap — not chased, not lied about.
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="italic text-muted-foreground"
            data-testid={`${testId}-not-required`}
          >
            Not required
          </span>
          {onRestoreRequired ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRestoreRequired();
              }}
              disabled={exemptionPending}
              className="text-xs underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testId}-restore-required`}
            >
              {exemptionPending ? "Saving…" : "Restore as required"}
            </button>
          ) : null}
        </span>
      ) : (
        <span
          className="italic text-muted-foreground"
          data-testid={`${testId}-missing`}
        >
          {pending ? "Uploading…" : "Not on file"}
        </span>
      )}

      {wired ? (
        <div className="ml-auto flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {!present && !exempt && onMarkNotRequired ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarkNotRequired();
              }}
              disabled={pending || exemptionPending}
              className="rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Mark ${label} not required`}
              data-testid={`${testId}-mark-not-required`}
            >
              Mark not required
            </button>
          ) : null}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPicker();
            }}
            disabled={pending}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={present ? `Replace ${label}` : `Upload ${label}`}
            data-testid={`${testId}-upload`}
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
          {present ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(slot);
              }}
              disabled={pending}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={`Delete ${label}`}
              data-testid={`${testId}-delete`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}

      {wired ? (
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={onFileSelected}
          data-testid={`${testId}-input`}
        />
      ) : null}
    </li>
  );
}
